import React from 'react';
import { RadioGroupProps } from '../../../types/DesignSystem/RadioGroup';
import Radio from '../Radio/Radio';
import FieldTitle from '../FieldTitle/FieldTitle';
import { FieldMessages, fieldInvalid, messageIds } from '../fieldHelper';
import { useControllableState } from '../useControllableState';

/**
 * RadioGroup (§ 303) — see the type file for why it exists.
 *
 * Nothing here is new drawing: the radios are `Radio`, the label is `FieldTitle`
 * (naming the group through `labelId` → `aria-labelledby`, because a `<label htmlFor>`
 * cannot point at a group), the helper line is the one seam every field renders
 * through (`fieldHelper.tsx`, § 302). What is new is that the group is a THING: one
 * `name` (so the browser gives arrow keys and the single tab stop), `aria-required`,
 * `aria-invalid` and the described-by wiring on the element that carries the role.
 */
const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup({
  label,
  options,
  value: valueProp,
  defaultValue,
  onChange,
  name,
  orientation = 'vertical',
  size = 'md',
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  errorMessage,
  hideLabel = false,
  labelSize,
  className = '',
  'data-testid': dataTestId,
  'aria-describedby': describedByProp,
  ...rest
}, ref) {
  const uid = React.useId();
  const groupName = name ?? `${uid}-radio`;
  const labelId = `${uid}-label`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  /* § 356 / § 357 — see TextField. */
  const invalid = fieldInvalid(error, errorMessage);
  const [value, setValue] = useControllableState<string | number | undefined>(valueProp, defaultValue, undefined);
  const describedBy = [describedByProp, ...messageIds({ helperId, errorId }, { helperText, errorMessage })]
    .filter(Boolean).join(' ') || undefined;

  return (
    <div className={className}>
      {label !== '' && (
        <FieldTitle
          label={label}
          required={required}
          /* § 355 — hidden, not removed: it still names the group. */
          className={hideLabel ? 'tw-sr-only' : 'tw-mb-2'}
          size={labelSize}
          disabled={disabled}
          labelId={labelId}
        />
      )}
      <div
        /* rest first — the Button.tsx rule; the role and its wiring below are the component's. */
        {...rest}
        ref={ref}
        role="radiogroup"
        aria-labelledby={label !== '' ? labelId : undefined}
        /* `aria-required` on the GROUP, not the inputs: the requirement is "pick one". */
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        data-orientation={orientation}
        data-testid={dataTestId}
        className={orientation === 'horizontal' ? 'tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-3' : 'tw-flex tw-flex-col tw-gap-3'}
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            name={groupName}
            value={option.value}
            label={option.label}
            size={size}
            checked={value === option.value}
            disabled={disabled || Boolean(option.disabled)}
            onChange={(next) => { setValue(next); onChange?.(next); }}
            data-testid={option['data-testid'] ?? (dataTestId ? `${dataTestId}-option-${option.value}` : undefined)}
          />
        ))}
      </div>
      <FieldMessages
        helperId={helperId}
        errorId={errorId}
        helperText={helperText}
        errorMessage={errorMessage}
        error={error}
        className="tw-mt-2"
        data-testid={dataTestId}
      />
    </div>
  );
});

export default RadioGroup;
